import React, { useMemo } from 'react';
import { LAYER_IMAGES, RACE_CONFIG, COLOR_CONFIG, FACTION_CONFIG } from '../../lib/gameConstants';
import { useAssetLayers } from '../../contexts/AssetLayersContext';
import { buildLayerComponents } from '../utils/layerRenderer';

interface CharacterAvatarProps {
  raceName: string;
  colorName: string;
  factionName?: string;
  size?: 'small' | 'medium' | 'large' | number;
  showPlanet?: boolean;
  planetWrapperClassName?: string;
  disablePlanetAnimation?: boolean;
  className?: string;
  onClick?: () => void;
  // 基礎圖層 URL（語義化命名，按 layer_order）
  layerBackgroundUrl?: string;
  layerBodyUrl?: string;
  layerSkinUrl?: string;
  layerEyesUrl?: string;
  layerTattooUrl?: string;
  layerOutfitUrl?: string;
  layerFrameUrl?: string;
  // 裝備圖層 URL（語義化命名）
  layerArmorId?: string;
  layerArmorUrl?: string;
  layerWeaponUrl?: string;
  layerShieldUrl?: string;
  layerAccessoryUrl?: string;
  planetImageUrl?: string;
}

/**
 * 為 Supabase Storage 圖片 URL 添加 Image Transformation 參數
 * 根據顯示尺寸智能選擇縮圖大小以優化載入性能
 *
 * 轉換策略：
 * - size ≤ 64px  → 128x128 縮圖（Mining Map 小頭像）
 * - 64 < size ≤ 128px → 256x256 縮圖（中等尺寸）
 * - size > 128px → 原圖 512x512（大尺寸顯示）
 *
 * @param url - 原始圖片 URL
 * @param displaySize - 實際顯示尺寸（像素）
 * @returns 帶有 transform 參數的優化 URL，或原始 URL（非 Supabase Storage）
 */
const addImageTransform = (url: string | undefined, displaySize: number): string | undefined => {
  if (!url || url === 'null' || url === 'undefined') return url;

  // 只處理 Supabase Storage URL
  const isSupabaseStorage = url.includes('.supabase.co/storage/v1/object/public/');
  if (!isSupabaseStorage) return url;

  // 避免重複添加 transform 參數
  if (url.includes('width=') || url.includes('height=')) return url;

  // 根據顯示尺寸選擇最佳縮圖大小
  let targetSize: number;
  if (displaySize <= 64) {
    targetSize = 128;  // Mining map 等小頭像
  } else if (displaySize <= 128) {
    targetSize = 256;  // 中等尺寸
  } else {
    return url;  // 大尺寸使用原圖（> 128px）
  }

  // 使用 /render/image/ 端點並添加 transform 參數
  const renderUrl = url.replace(
    '/storage/v1/object/public/',
    '/storage/v1/render/image/public/'
  );

  // 添加 transform 參數：resize=contain 保持比例，自動 WebP 優化
  return `${renderUrl}?width=${targetSize}&height=${targetSize}&resize=contain`;
};

export const CharacterAvatar: React.FC<CharacterAvatarProps> = ({
  raceName,
  colorName,
  factionName,
  size = 'medium',
  showPlanet = false,
  planetWrapperClassName = 'scale-150 opacity-40',
  disablePlanetAnimation = false,
  className = '',
  onClick,
  layerBackgroundUrl,
  layerBodyUrl,
  layerSkinUrl,
  layerEyesUrl,
  layerTattooUrl,
  layerOutfitUrl,
  layerFrameUrl,
  layerArmorId,
  layerArmorUrl,
  layerWeaponUrl,
  layerShieldUrl,
  layerAccessoryUrl,
  planetImageUrl
}) => {
  const { layers, isLoading, error } = useAssetLayers();

  const safeRaceName = raceName || 'SIRIANS';
  const safeColorName = colorName || 'bubble';

  const raceKey = safeRaceName.toUpperCase() as keyof typeof RACE_CONFIG;
  const colorKey = safeColorName.toLowerCase() as keyof typeof COLOR_CONFIG;
  const factionKey = factionName?.toLowerCase() as keyof typeof FACTION_CONFIG;

  const raceConfig = RACE_CONFIG[raceKey];
  const colorConfig = COLOR_CONFIG[colorKey];
  const factionConfig = factionKey ? FACTION_CONFIG[factionKey] : null;

  if (!raceConfig) {
    console.error('[CharacterAvatar] Invalid race name:', raceName, '- using SIRIANS as fallback');
  }

  if (!colorConfig) {
    console.error('[CharacterAvatar] Invalid color name:', colorName, '- using bubble as fallback');
  }

  // 計算實際顯示尺寸（像素）用於 Image Transformation
  const displaySize = typeof size === 'number' ? size : (
    size === 'small' ? 96 :   // w-24 = 96px
    size === 'large' ? 256 :   // w-64 = 256px
    192                        // medium: w-48 = 192px
  );

  // 對所有圖層 URL 應用 Image Transformation（使用 useMemo 避免重複計算）
  const optimizedLayerBackgroundUrl = useMemo(() => addImageTransform(layerBackgroundUrl, displaySize), [layerBackgroundUrl, displaySize]);
  const optimizedLayerBodyUrl = useMemo(() => addImageTransform(layerBodyUrl, displaySize), [layerBodyUrl, displaySize]);
  const optimizedLayerSkinUrl = useMemo(() => addImageTransform(layerSkinUrl, displaySize), [layerSkinUrl, displaySize]);
  const optimizedLayerEyesUrl = useMemo(() => addImageTransform(layerEyesUrl, displaySize), [layerEyesUrl, displaySize]);
  const optimizedLayerTattooUrl = useMemo(() => addImageTransform(layerTattooUrl, displaySize), [layerTattooUrl, displaySize]);
  const optimizedLayerOutfitUrl = useMemo(() => addImageTransform(layerOutfitUrl, displaySize), [layerOutfitUrl, displaySize]);
  const optimizedLayerFrameUrl = useMemo(() => addImageTransform(layerFrameUrl, displaySize), [layerFrameUrl, displaySize]);
  const optimizedLayerArmorUrl = useMemo(() => addImageTransform(layerArmorUrl, displaySize), [layerArmorUrl, displaySize]);
  const optimizedLayerWeaponUrl = useMemo(() => addImageTransform(layerWeaponUrl, displaySize), [layerWeaponUrl, displaySize]);
  const optimizedLayerShieldUrl = useMemo(() => addImageTransform(layerShieldUrl, displaySize), [layerShieldUrl, displaySize]);
  const optimizedLayerAccessoryUrl = useMemo(() => addImageTransform(layerAccessoryUrl, displaySize), [layerAccessoryUrl, displaySize]);
  const optimizedPlanetImageUrl = useMemo(() => addImageTransform(planetImageUrl, displaySize), [planetImageUrl, displaySize]);

  const skinImage = optimizedLayerSkinUrl || colorConfig?.skinImage || LAYER_IMAGES.skins.eclipse;
  const eyesImage = optimizedLayerEyesUrl || LAYER_IMAGES.eyes.default;
  const outfitImage = optimizedLayerOutfitUrl || LAYER_IMAGES.outfits.default;
  const frameImage = optimizedLayerFrameUrl || raceConfig?.frameImage || LAYER_IMAGES.frames.sirians;
  const planetImage = optimizedPlanetImageUrl || raceConfig?.planetImage;

  const hasArmorId = Boolean(
    layerArmorId &&
    layerArmorId !== 'null' &&
    layerArmorId !== 'undefined'
  );

  const hasArmorUrl = Boolean(
    optimizedLayerArmorUrl &&
    optimizedLayerArmorUrl !== 'null' &&
    optimizedLayerArmorUrl !== 'undefined' &&
    optimizedLayerArmorUrl.trim() !== ''
  );

  const hasArmor = hasArmorUrl || hasArmorId;
  const shouldShowBody = !hasArmorUrl;

  const armorRenderKey = optimizedLayerArmorUrl || 'no-armor';
  const bodyRenderKey = (shouldShowBody && optimizedLayerBodyUrl) ? `body-${optimizedLayerBodyUrl}` : 'body-hidden';

  const renderLegacyLayers = (): React.ReactElement[] => {
    const legacyLayers: React.ReactElement[] = [];

    if (optimizedLayerBackgroundUrl) {
      legacyLayers.push(
        <img
          key="layer_background"
          src={optimizedLayerBackgroundUrl}
          alt="背景"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          aria-hidden="true"
        />
      );
    }

    if (optimizedLayerBodyUrl && shouldShowBody) {
      legacyLayers.push(
        <img
          key={bodyRenderKey}
          src={optimizedLayerBodyUrl}
          alt="身體"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          aria-hidden="true"
        />
      );
    }

    legacyLayers.push(
      <img
        key="layer_skin"
        src={skinImage}
        alt="皮膚"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        aria-hidden="true"
      />
    );

    if (optimizedLayerTattooUrl) {
      legacyLayers.push(
        <img
          key="layer_tattoo"
          src={optimizedLayerTattooUrl}
          alt="刺青"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          aria-hidden="true"
        />
      );
    }

    legacyLayers.push(
      <img
        key="layer_eyes"
        src={eyesImage}
        alt="眼睛"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        aria-hidden="true"
      />
    );

    legacyLayers.push(
      <img
        key="layer_outfit"
        src={outfitImage}
        alt="服裝"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        aria-hidden="true"
      />
    );

    legacyLayers.push(
      <img
        key="layer_frame"
        src={frameImage}
        alt="邊框"
        className="absolute inset-0 w-full h-full object-contain pointer-events-none"
        aria-hidden="true"
      />
    );

    if (optimizedLayerArmorUrl) {
      legacyLayers.push(
        <img
          key={armorRenderKey}
          src={optimizedLayerArmorUrl}
          alt="護甲"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ zIndex: 10 }}
          aria-hidden="true"
        />
      );
    }

    if (optimizedLayerWeaponUrl) {
      legacyLayers.push(
        <img
          key="layer_weapon"
          src={optimizedLayerWeaponUrl}
          alt="武器"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ zIndex: 10 }}
          aria-hidden="true"
        />
      );
    }

    if (optimizedLayerShieldUrl) {
      legacyLayers.push(
        <img
          key="layer_shield"
          src={optimizedLayerShieldUrl}
          alt="盾牌"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ zIndex: 10 }}
          aria-hidden="true"
        />
      );
    }

    if (optimizedLayerAccessoryUrl) {
      legacyLayers.push(
        <img
          key="layer_accessory"
          src={optimizedLayerAccessoryUrl}
          alt="飾品"
          className="absolute inset-0 w-full h-full object-contain pointer-events-none"
          style={{ zIndex: 10 }}
          aria-hidden="true"
        />
      );
    }

    return legacyLayers;
  };

  const layerComponents = useMemo(() => {
    if (!layers || layers.length === 0 || error) {
      console.warn('⚠️ [CharacterAvatar] Using legacy rendering');
      return renderLegacyLayers();
    }

    console.log('👤 [CharacterAvatar] Using dynamic rendering');
    return buildLayerComponents({
      character: {
        layer_background_url: optimizedLayerBackgroundUrl,
        layer_body_url: optimizedLayerBodyUrl,
        layer_skin_url: optimizedLayerSkinUrl,
        layer_eyes_url: optimizedLayerEyesUrl,
        layer_tattoo_url: optimizedLayerTattooUrl,
        layer_outfit_url: optimizedLayerOutfitUrl,
        layer_frame_url: optimizedLayerFrameUrl,
        layer_armor_id: layerArmorId,
        layer_armor_url: optimizedLayerArmorUrl,
        layer_weapon_url: optimizedLayerWeaponUrl,
        layer_shield_url: optimizedLayerShieldUrl,
        layer_accessory_url: optimizedLayerAccessoryUrl,
      },
      layers,
      raceName: safeRaceName,
      colorName: safeColorName
    });
  }, [
    layers,
    error,
    optimizedLayerBackgroundUrl,
    optimizedLayerBodyUrl,
    optimizedLayerSkinUrl,
    optimizedLayerEyesUrl,
    optimizedLayerTattooUrl,
    optimizedLayerOutfitUrl,
    optimizedLayerFrameUrl,
    layerArmorId,
    optimizedLayerArmorUrl,
    optimizedLayerWeaponUrl,
    optimizedLayerShieldUrl,
    optimizedLayerAccessoryUrl,
    safeRaceName,
    safeColorName,
    skinImage,
    eyesImage,
    outfitImage,
    frameImage,
    shouldShowBody,
    armorRenderKey,
    bodyRenderKey
  ]);

  const getSizeClass = () => {
    if (typeof size === 'number') {
      return '';
    }
    const sizeClasses = {
      small: 'w-24 h-24',
      medium: 'w-48 h-48',
      large: 'w-64 h-64'
    };
    return sizeClasses[size];
  };

  const getSizeStyle = () => {
    if (typeof size === 'number') {
      return { width: `${size}px`, height: `${size}px` };
    }
    return {};
  };

  const planetClassName = `w-full h-full object-contain ${disablePlanetAnimation ? '' : 'animate-rotate-planet-slow'}`;

  return (
    <div className={`relative ${className}`}>
      {showPlanet && planetImage && (
        // pointer-events-none to avoid blocking taps on iOS
        <div className={`absolute inset-0 flex items-center justify-center z-0 pointer-events-none ${planetWrapperClassName}`} aria-hidden="true">
          <img
            src={planetImage}
            alt={`${raceConfig?.displayName || raceName} 行星`}
            className={`${planetClassName} pointer-events-none`}
            aria-hidden="true"
          />
        </div>
      )}

      <div
        className={`relative z-10 ${getSizeClass()} ${onClick ? 'cursor-pointer hover:scale-105 transition-transform' : ''}`}
        style={getSizeStyle()}
        onClick={onClick}
      >
        {layerComponents}
      </div>
    </div>
  );
};
